import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { ToggleMenu } from '@ignitesales/neuro-application/src/neuro-ui';
import {useGuideConfig, NeuroMessage, useDispatch, useNeuroState, useNeuroMessages} from '@ignitesales/neuro-application';
import {setDialogueGuideConfiguration, setThemeConfiguration} from "../../state/DialogueActions.mjs";
import ThemeSettingsInput from "./ThemeSettingsInput";
import {convertConfigValueToCorrectType, dialogueGuideSettingsManifest, getValidThemeGroupConfigs} from "../../utils/themeHelper.mjs";

const NAV = {
    DIALOGUE_SETTINGS: 'dialogueGuideSettings',
    THEME_SETTINGS: 'themeSettings'
};

const ThemeRunnerTool = ({ enableMenu, onMenuClose }) => {
    const { isMockMode, runMode } = useGuideConfig();
    const { themeSettings, themeManifest, dialogueGuideSettings } = useNeuroState();
    const { getMessage } = useNeuroMessages();
    const dispatch = useDispatch();
    const [showMenu, setShowMenu] = useState(false);
    const [localThemeSettings, setLocalThemeSettings] = useState(themeSettings);
    const [localDialogueSettings, setLocalDialogueSettings] = useState(dialogueGuideSettings);
    const [activeNav, setActiveNav] = useState(NAV.DIALOGUE_SETTINGS);
    const componentClass = 'themeRunnerTool';
    const menuIsOpen = showMenu || enableMenu;
    const isDevelopmentMode = runMode === 'development';
    const toggleButtonClass = classnames(`${componentClass}__menuBtn`, {
        menuIsOpen: menuIsOpen,
    });

    useEffect(() => {
        setLocalThemeSettings(themeSettings);
    }, [themeSettings]);

    const onToolMenuClose = () => {
        setShowMenu(false);
        onMenuClose && onMenuClose();
    };

    const filterEmptyThemeGroups = groupKey => {
        const group = localThemeSettings[groupKey];
        return Object.keys(group).length > 0;
    };

    const onThemeSettingsFieldChange = (newValue, groupKey, fieldConfig) => {
        const { key, type } = fieldConfig;
        const fieldKey = type === 'PageTypes' ? 'pageTypes' : key;
        setLocalThemeSettings({
            ...localThemeSettings,
            [groupKey]: {
                ...localThemeSettings[groupKey],
                [fieldKey]: convertConfigValueToCorrectType(newValue)
            }
        });
    };

    const onGuideSettingsFieldChange = (key, newValue) => {
        setLocalDialogueSettings({
            ...localDialogueSettings,
            [key]: convertConfigValueToCorrectType(newValue)
        });
    };

    const onThemeSettingsSubmit = () => {
        dispatch( setThemeConfiguration(localThemeSettings) );
    };

    const onDialogueSettingsSubmit = () => {
        dispatch( setDialogueGuideConfiguration(localDialogueSettings) );
    };

    return (
        (isMockMode || isDevelopmentMode) && (
            <>
                {enableMenu === undefined && (
                    <button role="button" title="Theme Runner Menu" className={toggleButtonClass} onClick={() => setShowMenu(!showMenu)}>
                        <i className="fa fa-paint-brush" />
                    </button>
                )}

                <ToggleMenu className={componentClass} animation="slideFromRight" isOpen={menuIsOpen} onOuterClick={onToolMenuClose}>
                    <i className={`fa fa-times ${componentClass}__closeBtn`} onClick={onToolMenuClose}/>
                    <div className={`${componentClass}__header`} role="complementary" aria-label="Development Tool Header">
                        <NeuroMessage id="themeTool.subtitle" tagName="span" />
                        <NeuroMessage id="themeTool.title" tagName="h1" />
                        <nav>
                            <span className={activeNav === NAV.DIALOGUE_SETTINGS && 'active'} onClick={()=>setActiveNav(NAV.DIALOGUE_SETTINGS)}>Dialogue Guide Settings</span>
                            <span className={activeNav === NAV.THEME_SETTINGS && 'active'} onClick={()=>setActiveNav(NAV.THEME_SETTINGS)}>Theme Settings</span>
                        </nav>
                    </div>

                    <div className={`${componentClass}__content`}>
                        <div>
                            { activeNav === NAV.DIALOGUE_SETTINGS &&
                                <div id="dialogueGuideSettings">
                                    {Object.values(dialogueGuideSettingsManifest).map(config => {
                                        const {key, label, type, options, defaultValue} = config;
                                        const currentValue = localDialogueSettings?.[key];
                                        return <ThemeSettingsInput
                                            value={currentValue  || currentValue === false ? currentValue : defaultValue}
                                            key={key}
                                            label={label}
                                            name={key}
                                            onChange={v => onGuideSettingsFieldChange(key, v)}
                                            options={options || []}
                                            type={type.toLocaleLowerCase()}
                                        />
                                    })}
                                    <button className={`${componentClass}__submitBtn btn`} onClick={onDialogueSettingsSubmit}>Submit</button>
                                </div>
                            }
                            { activeNav === NAV.THEME_SETTINGS &&
                                <div id="themeSettings">
                                    {Object.keys(themeManifest).filter(filterEmptyThemeGroups).map(groupKey => {
                                        const currentGroup = getValidThemeGroupConfigs(themeManifest[groupKey], localThemeSettings[groupKey]);
                                        return <div className={`${componentClass}__themeSettingsGroup`}>
                                            <h5>{groupKey} settings</h5>
                                            {Object.values(currentGroup).map((config) => {
                                                const {key, label, type, options, isLang} = config;
                                                const isDropdown = type === 'PageTypes' || type === 'Dropdown';
                                                const currentValue = localThemeSettings?.[groupKey]?.[key];
                                                return <ThemeSettingsInput
                                                    value={isLang ? getMessage(key) : currentValue}
                                                    disabled={isLang}
                                                    key={key}
                                                    label={isLang ? `${label} (key = ${key})` : label}
                                                    name={key}
                                                    onChange={v => onThemeSettingsFieldChange(v, groupKey, config)}
                                                    options={options || []}
                                                    type={isDropdown ? 'dropdown' : type.toLocaleLowerCase()}
                                                />
                                            })}
                                        </div>
                                    })}
                                    <button className={`${componentClass}__submitBtn btn`} onClick={onThemeSettingsSubmit}>Submit</button>
                                </div>
                            }
                        </div>
                    </div>
                </ToggleMenu>
            </>
        )
    );
};

ThemeRunnerTool.propTypes = {
    enableForAnyEnvironment: PropTypes.bool,
    enableMenu: PropTypes.bool,
    isConversationTestPage: PropTypes.bool,
    onMenuClose: PropTypes.func,
};
export default ThemeRunnerTool;
